import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useThemeContext } from "../../contexts/ThemeContext";
import { checkShopifyPricingStatus } from "../../services/shopifyApi";

/**
 * Landing page for every first-time Shopify shop connection — the actual fix for App
 * Store requirement 2.3.3. The OAuth callback redirects here (ClusterDen's own domain)
 * immediately, and only from here do we decide whether to send the merchant on to
 * Shopify's hosted pricing page, or straight to /welcome / /signup. See
 * shopifyOAuthController.js's redirectToConnecting and exports.pricingCheck.
 */
const ShopifyConnecting = () => {
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";
  const [error, setError] = useState("");
  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";
  // React.StrictMode (dev) runs effects twice — the check must fire once per attempt.
  const inFlight = useRef(false);

  const run = React.useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError("");
    if (!shop) {
      navigate("/", { replace: true });
      return;
    }
    const q = `?shop=${encodeURIComponent(shop)}`;
    try {
      const result = await checkShopifyPricingStatus(shop);
      if (result?.action === "pricing" && result.pricingUrl) {
        window.location.href = result.pricingUrl;
      } else if (result?.action === "app") {
        // Known shop (reconnect/reinstall) — nothing to sign up for, straight back into the
        // app, same target the OAuth callback's own reconnect redirect uses.
        window.location.href = "/?shopify=connected";
      } else if (result?.action === "welcome") {
        navigate(`/welcome${q}`, { replace: true });
      } else {
        // "signup" — Partner API not configured on this environment
        navigate(`/signup${q}`, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not finish connecting your Shopify store.");
      inFlight.current = false;
    }
  }, [shop, navigate]);

  useEffect(() => {
    run();
  }, [run]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 3,
        textAlign: "center",
        backgroundColor: mode === "dark" ? "#0a0a0a" : "#ffffff",
      }}
    >
      {error ? (
        <>
          <Typography sx={{ color: "#ef4444", fontSize: "14px" }}>{error}</Typography>
          <Button onClick={run} variant="contained" sx={{ backgroundColor: accentColor, textTransform: "none" }}>
            Try again
          </Button>
        </>
      ) : (
        <>
          <CircularProgress size={32} sx={{ color: accentColor }} />
          <Typography
            sx={{ fontSize: "15px", color: mode === "dark" ? "#f5f5f5" : "#121a34", fontFamily: "Instrument Sans, sans-serif" }}
          >
            Connecting your Shopify store…
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ShopifyConnecting;
